/**
 * Google Analytics 4 - Event Tracking Utilities
 * 
 * Este arquivo contém funções para rastrear eventos personalizados
 * no Google Analytics 4 (GA4).
 * 
 * Uso:
 * import { trackServiceView, trackContactClick } from '../utils/analytics'
 * 
 * trackServiceView('AI Consulting')
 * trackContactClick('WhatsApp', 'AI Consulting')
 */

/**
 * Função genérica para rastrear eventos no GA4
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  customParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...customParams
    })
  }
}

/**
 * Rastrear visualização de página de serviço
 */
export const trackServiceView = (serviceName: string) => {
  trackEvent('view_service', 'Services', serviceName, undefined, {
    service_name: serviceName,
    page_path: window.location.pathname
  })
}

/**
 * Rastrear clique em botão de contato
 */
export const trackContactClick = (contactType: string, servicePage?: string) => {
  trackEvent('contact_click', 'Contact', `${contactType} - ${servicePage || 'Unknown'}`, undefined, {
    contact_type: contactType,
    service_page: servicePage || 'Unknown',
    page_path: window.location.pathname
  })
}

/**
 * Rastrear download de arquivo
 */
export const trackDownload = (fileName: string, pageLocation?: string) => {
  trackEvent('download', 'Downloads', `${fileName} - ${pageLocation || 'Unknown'}`, undefined, {
    file_name: fileName,
    page_location: pageLocation || window.location.pathname
  })
}

/**
 * Rastrear submissão de formulário
 */
export const trackFormSubmit = (formName: string, formData?: Record<string, any>) => {
  trackEvent('form_submit', 'Forms', formName, undefined, {
    form_name: formName,
    page_path: window.location.pathname,
    ...formData
  })
}

/**
 * Rastrear clique no WhatsApp Float
 */
export const trackWhatsAppClick = (pageLocation: string) => {
  trackEvent('whatsapp_click', 'Contact', `WhatsApp - ${pageLocation}`, undefined, {
    contact_method: 'WhatsApp',
    page_path: pageLocation
  })
}

/**
 * Rastrear clique em item do menu
 */
export const trackMenuClick = (menuItem: string, menuType: string) => {
  trackEvent('menu_click', 'Navigation', menuItem, undefined, {
    menu_item: menuItem,
    menu_type: menuType,
    page_path: window.location.pathname
  })
}

/**
 * Rastrear mudança de idioma
 */
export const trackLanguageSwitch = (fromLang: string, toLang: string) => {
  trackEvent('language_switch', 'i18n', `${fromLang} to ${toLang}`, undefined, {
    from_language: fromLang,
    to_language: toLang,
    page_path: window.location.pathname
  })
}

/**
 * Rastrear clique em CTA (Call to Action)
 */
/**
 * `ctaId` é um identificador ESTÁVEL em inglês (ex.: 'genesis.cta.hero') — nunca o texto traduzido, que muda com o
 * copy e com o idioma e quebra a série histórica (docs/analytics-events.md). O texto visível é opcional e vai como parâmetro.
 */
export const trackCTAClick = (ctaId: string, ctaLocation: string, servicePage?: string, ctaText?: string) => {
  trackEvent('cta_click', 'CTA', ctaId, undefined, {
    cta_id: ctaId,
    cta_text: ctaText,
    cta_location: ctaLocation,
    service_page: servicePage,
    page_path: window.location.pathname
  })
}

/**
 * Rastrear visualização de produto
 */
export const trackProductView = (productName: string) => {
  trackEvent('view_product', 'Products', productName, undefined, {
    product_name: productName,
    page_path: window.location.pathname
  })
}

/**
 * Rastrear tempo de permanência na página
 */
export const trackPageTime = (pagePath: string, timeInSeconds: number) => {
  trackEvent('page_time', 'Engagement', pagePath, Math.round(timeInSeconds), {
    page_path: pagePath,
    time_seconds: Math.round(timeInSeconds)
  })
}

/**
 * Rastrear scroll depth (profundidade de rolagem)
 */
export const trackScrollDepth = (pagePath: string, depth: number) => {
  trackEvent('scroll_depth', 'Engagement', pagePath, depth, {
    page_path: pagePath,
    scroll_depth: depth
  })
}

/**
 * Rastrear visualização de seção específica
 */
export const trackSectionView = (sectionName: string, pagePath: string) => {
  trackEvent('section_view', 'Content', sectionName, undefined, {
    section_name: sectionName,
    page_path: pagePath
  })
}
